import * as moment from 'moment'
import { Utility } from '@nkr/services/utility';
import { IAdminData, INewFileUpload, IProfileData, ISerologyData, IServicesData, ISrtrData, IUnosData } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/organ-type';
import { IAdminDataValidation } from './edit/admin/validation';
import { IProfileDataValidation } from './edit/profile-data/validation';
import { ISerologyDataValidation } from './edit/serology/validation';
import { IServicesDataValidation } from './edit/services/validation';
import { ISrtrDataValidation } from './edit/srtr/validation';
import { IErrorItem } from './error-item';
import { isValidCoord } from './edit/admin/map-button';
import { IValidateAchRoutingNumberResponse } from '@nkr/store/index';

export const Validate = {
    validateAdminData: async (data: IAdminData, validateAchRoutingNumber: (achRoutingNumber: string) => Promise<IValidateAchRoutingNumberResponse>) => {
        let { alias, invoicePoStartDate, invoicePoExpirationDate, latitude, longitude } = data;
        let twoYearsAgo = moment().subtract(2, "year");

        let invoicePoStartDateHasValue = moment(invoicePoStartDate, 'YYYY-MM-DD', true).isValid();
        let invoicePoExpirationDateHasValue = moment(invoicePoExpirationDate, 'YYYY-MM-DD', true).isValid();

        let invoicePoStartDateValid = invoicePoStartDate != "" ? invoicePoStartDateHasValue && moment(invoicePoStartDate, 'YYYY-MM-DD', true) > twoYearsAgo : true;
        let invoicePoExpirationDateValid = invoicePoExpirationDate != "" ? invoicePoExpirationDateHasValue && moment(invoicePoExpirationDate, 'YYYY-MM-DD', true) > twoYearsAgo : true;
        let invoicePoStart_and_ExpirationDateValid = true;

        if (invoicePoStartDateHasValue && invoicePoExpirationDateHasValue) {
            invoicePoStart_and_ExpirationDateValid = moment(invoicePoExpirationDate, 'YYYY-MM-DD', true) >= moment(invoicePoStartDate, 'YYYY-MM-DD', true);
        }

        const neitherSet = (latitude === null || latitude === undefined) && (longitude === null || longitude === undefined);
        const latitudeValid = neitherSet || isValidCoord(latitude, 90);
        const longitudeValid = neitherSet || isValidCoord(longitude, 180);

        let validRoutingNumber = true;

        if (data.achRoutingNumber != '' && __ENVIRONMENT__ != 'dev') {
            let response = await validateAchRoutingNumber(data.achRoutingNumber);

            if (response.data.success == false) {
                validRoutingNumber = false;
            }
        }

        let validPackagingRoutingNumber = true;

        if (data.packagingAchRoutingNumber != '' && __ENVIRONMENT__ != 'dev') {
            let response = await validateAchRoutingNumber(data.packagingAchRoutingNumber);

            if (response.data.success == false) {
                validPackagingRoutingNumber = false;
            }
        }

        let validation: IAdminDataValidation = {
            alias: alias != "",
            latitude: latitudeValid,
            longitude: longitudeValid,
            invoicePoStartDate: invoicePoStartDateValid,
            invoicePoExpirationDate: invoicePoExpirationDateValid && invoicePoStart_and_ExpirationDateValid,
            routingNumber: validRoutingNumber,
            packagingRoutingNumber: validPackagingRoutingNumber
        };

        return validation;
    },
    isAdminDataValid: (validation: IAdminDataValidation) => {
        let { alias, invoicePoStartDate, invoicePoExpirationDate, routingNumber, packagingRoutingNumber } = validation;
        return alias && invoicePoStartDate && invoicePoExpirationDate && routingNumber && packagingRoutingNumber;
    },
    getInvalidAdminDataItems: (validation: IAdminDataValidation) => {
        let { alias, invoicePoStartDate, invoicePoExpirationDate, latitude, longitude, routingNumber, packagingRoutingNumber } = validation;

        let results: IErrorItem[] = [
            { field: 'Center Alias/Code', desc: 'Cannot be blank.', show: !alias },
            { field: 'Purchase Oders Start Date', desc: 'Date is not valid.', show: !invoicePoStartDate },
            { field: 'Purchase Oders Expiration Date', desc: 'Date is not valid, needs be be greater than Start Date.', show: !invoicePoExpirationDate },
            { field: 'Latitude', desc: 'Latitude is not valid.', show: !latitude },
            { field: 'Longitude', desc: 'Longitude is not valid.', show: !longitude },
            { field: 'ABA Routing Number', desc: 'Please specify a valid routing number', show: !routingNumber },
            { field: 'Packaging Routing Number', desc: 'Please specify a valid packaging routing number', show: !packagingRoutingNumber }
        ];

        return results;
    },

    validateSrtrData: (data: ISrtrData) => {
        let { dcAdultGraftSurvival1, dcAdultGraftSurvival3, dcPediatricGraftSurvival1,
            dcPediatricGraftSurvival3, adultGraftSurvival1, adultGraftSurvival3,
            pediatricGraftSurvival1, pediatricGraftSurvival3, dcAdultPatientSurvival1,
            dcAdultPatientSurvival3, dcPediatricPatientSurvival1, dcPediatricPatientSurvival3,
            adultPatientSurvival1, adultPatientSurvival3, pediatricPatientSurvival1, pediatricPatientSurvival3,
            srtrSourceDate, unosId } = data;

        let validation: ISrtrDataValidation = {
            dcAdultGraftSurvival1: dcAdultGraftSurvival1 > 0,
            dcAdultGraftSurvival3: dcAdultGraftSurvival3 > 0,
            dcPediatricGraftSurvival1: dcPediatricGraftSurvival1 > 0,
            dcPediatricGraftSurvival3: dcPediatricGraftSurvival3 > 0,
            adultGraftSurvival1: adultGraftSurvival1 > 0,
            adultGraftSurvival3: adultGraftSurvival3 > 0,
            pediatricGraftSurvival1: pediatricGraftSurvival1 > 0,
            pediatricGraftSurvival3: pediatricGraftSurvival3 > 0,
            dcAdultPatientSurvival1: dcAdultPatientSurvival1 > 0,
            dcAdultPatientSurvival3: dcAdultPatientSurvival3 > 0,
            dcPediatricPatientSurvival1: dcPediatricPatientSurvival1 > 0,
            dcPediatricPatientSurvival3: dcPediatricPatientSurvival3 > 0,
            adultPatientSurvival1: adultPatientSurvival1 > 0,
            adultPatientSurvival3: adultPatientSurvival3 > 0,
            pediatricPatientSurvival1: pediatricPatientSurvival1 > 0,
            pediatricPatientSurvival3: pediatricPatientSurvival3 > 0,
            srtrSourceDate: srtrSourceDate != '' && moment(srtrSourceDate, 'YYYY-MM-DD').isValid(),
            centerUnosId: unosId != ''
        };

        return validation;
    },
    isSrtrDataValid: (validation: ISrtrDataValidation) => {
        return validation.adultGraftSurvival1 && validation.adultGraftSurvival3
            && validation.adultPatientSurvival1 && validation.adultPatientSurvival3
            && validation.dcAdultGraftSurvival1 && validation.dcAdultGraftSurvival1
            && validation.dcAdultPatientSurvival1 && validation.dcAdultPatientSurvival3
            && validation.dcPediatricGraftSurvival1 && validation.dcPediatricGraftSurvival3
            && validation.dcPediatricPatientSurvival1 && validation.dcPediatricPatientSurvival3
            && validation.pediatricGraftSurvival1 && validation.pediatricGraftSurvival3
            && validation.pediatricPatientSurvival1 && validation.pediatricPatientSurvival3
            && validation.srtrSourceDate && validation.centerUnosId;
    },
    getInvalidSrtrDataItems: (validation: ISrtrDataValidation) => {
        let { dcAdultGraftSurvival1, dcAdultGraftSurvival3, dcPediatricGraftSurvival1, dcPediatricGraftSurvival3, adultGraftSurvival1, adultGraftSurvival3,
            pediatricGraftSurvival1, pediatricGraftSurvival3, dcAdultPatientSurvival1, dcAdultPatientSurvival3,
            dcPediatricPatientSurvival1, dcPediatricPatientSurvival3, adultPatientSurvival1, adultPatientSurvival3,
            pediatricPatientSurvival1, pediatricPatientSurvival3, srtrSourceDate, centerUnosId
        } = validation;

        let results: IErrorItem[] = [
            { field: 'Graft Survival/Deceased Donor: 1 Year Adult Graft Survival', desc: 'Must be greater than zero.', show: !dcAdultGraftSurvival1 },
            { field: 'Graft Survival/Deceased Donor: 3 Year Adult Graft Survival', desc: 'Must be greater than zero.', show: !dcAdultGraftSurvival3 },
            { field: 'Graft Survival/Deceased Donor: 1 Year Pediatric Graft Survival', desc: 'Must be greater than zero.', show: !dcPediatricGraftSurvival1 },
            { field: 'Graft Survival/Deceased Donor: 3 Year Pediatric Graft Survival', desc: 'Must be greater than zero.', show: !dcPediatricGraftSurvival3 },

            { field: 'Graft Survival/Living Donor: 1 Year Adult Graft Survival', desc: 'Must be greater than zero.', show: !adultGraftSurvival1 },
            { field: 'Graft Survival/Living Donor: 3 Year Adult Graft Survival', desc: 'Must be greater than zero.', show: !adultGraftSurvival3 },
            { field: 'Graft Survival/Living Donor: 1 Year Pediatric Graft Survival', desc: 'Must be greater than zero.', show: !pediatricGraftSurvival1 },
            { field: 'Graft Survival/Living Donor: 3 Year Pediatric Graft Survival', desc: 'Must be greater than zero.', show: !pediatricGraftSurvival3 },

            { field: 'Patient Survival/Deceased Donor: 1 Year Adult Patient Survival', desc: 'Must be greater than zero.', show: !dcAdultPatientSurvival1 },
            { field: 'Patient Survival/Deceased Donor: 3 Year Adult Patient Survival', desc: 'Must be greater than zero.', show: !dcAdultPatientSurvival3 },
            { field: 'Patient Survival/Deceased Donor: 1 Year Pediatric Patient Survival', desc: 'Must be greater than zero.', show: !dcPediatricPatientSurvival1 },
            { field: 'Patient Survival/Deceased Donor: 3 Year Pediatric Patient Survival', desc: 'Must be greater than zero.', show: !dcPediatricPatientSurvival3 },

            { field: 'Patient Survival/Living Donor: 1 Year Adult Patient Survival', desc: 'Must be greater than zero.', show: !adultPatientSurvival1 },
            { field: 'Patient Survival/Living Donor: 3 Year Adult Patient Survival', desc: 'Must be greater than zero.', show: !adultPatientSurvival3 },
            { field: 'Patient Survival/Living Donor: 1 Year Pediatric Patient Survival', desc: 'Must be greater than zero.', show: !pediatricPatientSurvival1 },
            { field: 'Patient Survival/Living Donor: 3 Year Pediatric Patient Survival', desc: 'Must be greater than zero.', show: !pediatricPatientSurvival3 },

            { field: 'SRTR Source Date', desc: 'This is required', show: !srtrSourceDate },
            { field: 'UNOS Center ID', desc: 'This is required', show: !centerUnosId },
        ];

        return results;
    },

    validateProfileData: (data: IProfileData) => {
        let { name, webUrl, earliestOrganPickupTime, latestOrganPickupTime, timezone, taxExemptExpirationDate, salesTaxFile, isTaxExempt, leadCoordinatorId } = data;
        const today = moment().format('MM/DD/YYYY');

        let earliestOrganPickupTimeValid = 1; // valid

        let earliestPickupTime = moment('2001-01-01 ' + earliestOrganPickupTime, 'YYYY-MM-DD HH:mm', true);

        if (earliestPickupTime.isValid()) {
            // needs to be between midnight and 10am
            earliestOrganPickupTimeValid = earliestPickupTime.isBetween(moment('2001-01-01 00:00'), moment('2001-01-01 10:00'), null, '[]') ? 1 : 2;
        } else {
            earliestOrganPickupTimeValid = 0; // can't be blank
        }

        let latestOrganPickupTimeValid = moment('2001-01-01 ' + latestOrganPickupTime, 'YYYY-MM-DD HH:mm', true).isValid();

        let validation: IProfileDataValidation = {
            name: name.trim() != '',
            webUrl: webUrl.trim() != '',
            earliestPickupTime: earliestOrganPickupTimeValid,
            latestOrganPickupTime: latestOrganPickupTimeValid,
            timezone: timezone != '',
            taxExemptExpirationDate: isTaxExempt ? taxExemptExpirationDate != '' && !moment(taxExemptExpirationDate).isBefore(today) : true,
            newTaxFile: isTaxExempt ? salesTaxFile.newFile != null ? true : salesTaxFile.files.length > 0 ? true : false : true,
            leadCoordinator: leadCoordinatorId > 0
        };

        return validation;
    },
    isProfileDataValid: (validation: IProfileDataValidation) => {
        let { name, webUrl, timezone, earliestPickupTime, latestOrganPickupTime, taxExemptExpirationDate, newTaxFile, leadCoordinator } = validation;
        return name && webUrl && timezone && latestOrganPickupTime && earliestPickupTime == 1 && taxExemptExpirationDate && newTaxFile && leadCoordinator;
    },
    getInvalidProfileDataItems: (validation: IProfileDataValidation) => {
        let { name, webUrl, earliestPickupTime, latestOrganPickupTime, timezone, taxExemptExpirationDate, leadCoordinator } = validation;

        let earliestPickupTimeDesc = 'Must be specified';

        if (earliestPickupTime == 2) {
            earliestPickupTimeDesc = 'Must be between midnight and 10am';
        }

        let results: IErrorItem[] = [
            { field: 'Center Name', desc: 'Must be specified', show: !name },
            { field: 'Web URL', desc: 'Must be specified', show: !webUrl },
            { field: 'Earliest Pickup Time', desc: earliestPickupTimeDesc, show: earliestPickupTime != 1 },
            { field: 'Latest Dropoff Time', desc: 'Must be specified', show: !latestOrganPickupTime },
            { field: 'Timezone', desc: 'Must select a value', show: !timezone },
            { field: 'Exemption Certificate Expiration Date', desc: 'Specify the exemption certificate expiration date', show: !taxExemptExpirationDate },
            { field: 'Lead Coordinator', desc: 'Must select a coordinator from the list', show: !leadCoordinator }
        ];

        return results;
    },

    validateServiceData: (data: IServicesData) => {
        let { maximumNddAge, minimumNddAge } = data;

        let validaton: IServicesDataValidation = {
            maximumNddAge: Utility.isNumber(maximumNddAge) ? parseInt(maximumNddAge) > 0 && parseInt(maximumNddAge) <= 99 : false,
            minimumNddAge: Utility.isNumber(minimumNddAge) ? parseInt(minimumNddAge) > 0 && parseInt(minimumNddAge) <= 99 : false
        };

        return validaton;
    },
    isServiceDataValid: (validation: IServicesDataValidation) => {
        return validation.maximumNddAge && validation.minimumNddAge;
    },
    getInvalidServiceDataItems: (data: IServicesDataValidation) => {
        let { minimumNddAge, maximumNddAge } = data;

        let results: IErrorItem[] = [
            { field: 'Minimum Age for Work Up', desc: 'Must select a value between 1 and 99.', show: !minimumNddAge },
            { field: 'Maximum Age for Work Up', desc: 'Must select a value between 1 and 99.', show: !maximumNddAge }
        ];

        return results;
    },

    validateSerologyData: (data: ISerologyData, organType?: OrganType) => {
        let { serologyMaxDaysBeforeSurgery, serologyShipToId } = data;

        let validation: ISerologyDataValidation = {
            serologyMaxDaysBeforeSurgery: serologyMaxDaysBeforeSurgery > -1,
            serologyShipToId: organType == OrganType.Liver || serologyShipToId > -1
        };

        return validation;
    },
    isSerologyDataValid: (validation: ISerologyDataValidation) => {
        let { serologyMaxDaysBeforeSurgery, serologyShipToId } = validation;
        return serologyMaxDaysBeforeSurgery && serologyShipToId;
    },
    getInvalidSerologyDataItems: (validation: ISerologyDataValidation) => {
        let { serologyMaxDaysBeforeSurgery, serologyShipToId } = validation;

        let results: IErrorItem[] = [
            { field: 'Max Days Before Surgery', desc: 'Please select a value.', show: !serologyMaxDaysBeforeSurgery },
            { field: 'Serology Shipping Address', desc: 'Please select a value.', show: !serologyShipToId }
        ];

        return results;
    },

    getFileInfo: (file: INewFileUpload) => {
        return {
            file: file != null ? file.file : null,
            version: file != null ? file.documentsVersionId : null,
            date: file != null ? file.date : ''
        };
    }
}